import { fabric } from "fabric";
import { DragCopyContext, DragCopyState } from "../types";
import { useBoardToolbarStore } from "../../../helper/store/toolbar";
import { generateLayerCid, rebuildObjectFromData, isImageOrVideoObject } from "../../../helper/layerUtils";
import { FILTER_PROCESS_TIME_BUDGET_MS } from "../config";
import { CANVAS_OBJECT_FIELDS } from "../../../../helper/canvasConfig";
import { ensureFabricVideoRegistered } from "../../../helper/video";
import { ensureFabricModel3dRegistered } from "../../../helper/model3d";

/**
 * =====================================================
 * Alt + 拖拽复制模块
 * =====================================================
 * 功能：
 * - 按住 Alt 键拖拽对象时，在原位保留 Ghost 占位符
 * - 释放鼠标后在终点创建副本，原对象回到原位
 * - 支持单对象和多选对象复制
 */

/**
 * 创建拖拽复制上下文
 */
export const createDragCopyContext = (): DragCopyContext => ({
  isDragging: false,
  isCopyMode: false,
  activeSelectionState: null,
  initialObjectStates: [],
  ghostObjects: [],
  ghostGenerationId: 0
});

/**
 * 同步克隆对象
 * 支持 Image、Group 和其他基础形状的同步克隆
 * @param obj 要克隆的对象
 * @returns 克隆后的对象，失败返回 null
 */
export const cloneObjectSync = (obj: fabric.Object): fabric.Object | null => {
  try {
    // 获取对象数据，使用 CANVAS_OBJECT_FIELDS 确保字段一致性
    const objectData = obj.toObject(CANVAS_OBJECT_FIELDS);
    // 移除 ID 和 Name 避免冲突
    delete objectData.id;
    delete objectData.name;

    let cloned: fabric.Object | null = null;

    // 处理 Image / Video
    if (isImageOrVideoObject(obj)) {
      const element = (obj as fabric.Image).getElement();
      // 只有当图片元素存在时才能同步克隆
      if (element) {
        if (obj.type === "video") {
          ensureFabricVideoRegistered();
          const VideoKlass = ((fabric as any).Video || fabric.Image) as any;
          cloned = new VideoKlass(element, { ...objectData, type: "video" });
          (cloned as any).type = "video";
        } else if (obj.type === "model3d") {
          ensureFabricModel3dRegistered();
          const Model3dKlass = ((fabric as any).Model3d || fabric.Image) as any;
          cloned = new Model3dKlass(element, { ...objectData, type: "model3d" });
          (cloned as any).type = "model3d";
        } else {
          cloned = new fabric.Image(element, objectData);
        }
      }
    }
    // 处理 Group
    else if (obj.type === "group") {
      const group = obj as fabric.Group;
      const children = group.getObjects();
      // 递归克隆子对象
      const clonedChildren = children
        .map((c) => cloneObjectSync(c))
        .filter((c): c is fabric.Object => c !== null);

      // 移除 objects 属性，因为我们会手动传递 children
      delete objectData.objects;
      cloned = new fabric.Group(clonedChildren, objectData);
    }
    // 处理其他基础形状
    else {
      if (!obj.type) return null;
      const className = obj.type.charAt(0).toUpperCase() + obj.type.slice(1);
      // @ts-ignore
      const FabricClass = fabric[className];
      if (FabricClass) {
        cloned = new FabricClass(objectData);
      }
    }

    return cloned;
  } catch (err) {
    console.warn("Sync clone failed for:", obj.type, err);
    return null;
  }
};

/**
 * 移除 Ghost 占位对象（带防御性清理）
 * @param canvas Fabric 画布实例
 * @param context 拖拽复制上下文
 */
export const removeGhosts = (
  canvas: fabric.Canvas,
  context: DragCopyContext
): void => {
  // 每次清理时，使之前的 clone 操作失效
  context.ghostGenerationId++;

  // 优先使用缓存的引用移除
  if (context.ghostObjects.length > 0) {
    const originalRenderOnAddRemove = canvas.renderOnAddRemove;
    canvas.renderOnAddRemove = false;
    try {
      context.ghostObjects.forEach((ghost) => {
        if (canvas.contains(ghost)) {
          canvas.remove(ghost);
        }
      });
    } finally {
      canvas.renderOnAddRemove = originalRenderOnAddRemove;
    }
    context.ghostObjects = [];
  }

  // 二次保险：遍历 Canvas 移除所有标记为 Ghost 的残留对象
  const objects = canvas.getObjects();
  const ghostsOnCanvas = objects.filter((obj) => (obj as any).isGhost);

  if (ghostsOnCanvas.length > 0) {
    const originalRenderOnAddRemove = canvas.renderOnAddRemove;
    canvas.renderOnAddRemove = false;
    try {
      ghostsOnCanvas.forEach((ghost) => {
        canvas.remove(ghost);
      });
    } finally {
      canvas.renderOnAddRemove = originalRenderOnAddRemove;
    }
  }

  canvas.requestRenderAll();
};

/**
 * 创建 Ghost 占位对象
 * 在拖拽复制时显示在原位，提供视觉反馈
 * @param canvas Fabric 画布实例
 * @param context 拖拽复制上下文
 */
export const createGhosts = (
  canvas: fabric.Canvas,
  context: DragCopyContext
): void => {
  // 先彻底清理，防止残留
  removeGhosts(canvas, context);

  // 更新 Generation ID
  context.ghostGenerationId++;
  context.ghostObjects = [];

  const originalRenderOnAddRemove = canvas.renderOnAddRemove;
  canvas.renderOnAddRemove = false;

  try {
    // 统一处理逻辑：基于 initialObjectStates 还原每个对象的世界状态
    context.initialObjectStates.forEach((state) => {
      const ghost = cloneObjectSync(state.object);

      if (ghost) {
        // 设置 Ghost 通用样式
        ghost.set({
          opacity: 1,
          evented: false,
          selectable: false,
          hasControls: false,
          hasBorders: false,
          canvas: canvas,
          // @ts-ignore
          isGhost: true,
          excludeFromExport: true
        });

        // 还原变换属性
        const initialDecomposed = fabric.util.qrDecompose(state.matrix);
        ghost.set({
          scaleX: initialDecomposed.scaleX,
          scaleY: initialDecomposed.scaleY,
          skewX: initialDecomposed.skewX,
          skewY: initialDecomposed.skewY,
          angle: initialDecomposed.angle,
          flipX: false,
          flipY: false
        });

        // 还原位置
        ghost.setPositionByOrigin(state.centerPoint, "center", "center");
        ghost.setCoords();

        context.ghostObjects.push(ghost);
        canvas.add(ghost);

        // 调整层级（尝试恢复到原对象层级）
        if (state.index >= 0) {
          ghost.moveTo(state.index);
        }
      }
    });
  } catch (error) {
    console.warn("创建 Ghost 失败:", error);
  } finally {
    canvas.renderOnAddRemove = originalRenderOnAddRemove;
    canvas.requestRenderAll();
  }
};

/**
 * 处理 Alt + 拖拽复制逻辑
 * 在对象移动过程中检测 Alt 键状态，控制 Ghost 显示/隐藏
 * @param canvas Fabric 画布实例
 * @param context 拖拽复制上下文
 * @param boardToolbarStore 工具栏状态 store
 * @param e 鼠标事件（可选）
 */
export const handleDragCopyMoving = (
  canvas: fabric.Canvas,
  context: DragCopyContext,
  boardToolbarStore: ReturnType<typeof useBoardToolbarStore>,
  e?: MouseEvent
): void => {
  // 标记对象正在拖拽，用于隐藏工具栏
  boardToolbarStore.setObjectDragging(true);

  // 普通拖拽不应该被阻止，只有 Alt 复制功能需要 initialObjectStates
  if (!context.isDragging) return;

  const isAltPressed = e?.altKey;

  // Alt 复制模式才需要检查 initialObjectStates
  if (isAltPressed && context.initialObjectStates.length === 0) return;

  if (isAltPressed && !context.isCopyMode) {
    // 进入复制模式：创建 Ghost
    context.isCopyMode = true;
    createGhosts(canvas, context);

    // 将正在拖拽的对象设置为半透明
    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      activeObject.set("opacity", 0.5);
      canvas.requestRenderAll();
    }
  } else if (!isAltPressed && context.isCopyMode) {
    // 退出复制模式：移除 Ghost
    context.isCopyMode = false;
    removeGhosts(canvas, context);

    // 恢复不透明
    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      activeObject.set("opacity", 1);
      canvas.requestRenderAll();
    }
  }
};

/**
 * 完成拖拽复制 - 创建副本
 * 在鼠标释放后执行，创建新副本并还原原对象位置
 * @param canvas Fabric 画布实例
 * @param context 拖拽复制上下文
 * @param currentInitialStates 拖拽前的初始状态记录
 */
export const completeDragCopy = (
  canvas: fabric.Canvas,
  context: DragCopyContext,
  currentInitialStates: DragCopyState[]
): void => {
  const originalRenderOnAddRemove = canvas.renderOnAddRemove;
  canvas.renderOnAddRemove = false;

  try {
    // 移除 Ghost
    removeGhosts(canvas, context);

    // 关键步骤：先取消所有选中状态，使对象回归世界坐标系
    canvas.discardActiveObject();

    const clonedObjects: fabric.Object[] = [];
    const pendingFilterTasks: fabric.Image[] = [];

    currentInitialStates.forEach((state) => {
      try {
        const currentObject = state.object;

        // 使用 toObject 获取属性，使用 CANVAS_OBJECT_FIELDS 确保字段一致性
        const objectData = currentObject.toObject(CANVAS_OBJECT_FIELDS);

        const newCid = generateLayerCid();
        // Alt + 拖拽复制时名称完全继承原元素，不再重新生成
        // @ts-ignore
        const newName = (state.object as any)?.name;

        // 使用公共函数重建对象，确保自定义属性正确复制
        const cloned = rebuildObjectFromData(objectData, {
          newId: newCid,
          newName: newName,
          imageElement: state.imageElement
        });

        if (!cloned) {
          console.warn("重建对象失败:", currentObject.type);
          return;
        }

        // 设置画布引用
        cloned.set({ canvas: canvas });

        // 复制滤镜
        if (isImageOrVideoObject(state.object) && (state.object as fabric.Image).filters?.length) {
          (cloned as fabric.Image).filters = [
            ...((state.object as fabric.Image).filters || [])
          ];
          pendingFilterTasks.push(cloned as fabric.Image);
        }

        clonedObjects.push(cloned);

        // 将原对象还原到初始位置
        state.object.setPositionByOrigin(state.centerPoint, "center", "center");
        state.object.setCoords();
      } catch (err) {
        console.error("处理单个对象复制失败", err);
      }
    });

    // 批量添加副本
    if (clonedObjects.length > 0) {
      canvas.add(...clonedObjects);

      // 选中新副本
      if (clonedObjects.length === 1) {
        canvas.setActiveObject(clonedObjects[0]);
      } else {
        const selection = new fabric.ActiveSelection(clonedObjects, { canvas });
        canvas.setActiveObject(selection);
      }

      // 异步分批处理滤镜
      if (pendingFilterTasks.length > 0) {
        const processFilters = () => {
          const start = performance.now();
          while (
            pendingFilterTasks.length > 0 &&
            performance.now() - start < FILTER_PROCESS_TIME_BUDGET_MS
          ) {
            const img = pendingFilterTasks.shift();
            if (img && img.canvas) {
              img.applyFilters();
            }
          }

          canvas.requestRenderAll();

          if (pendingFilterTasks.length > 0) {
            requestAnimationFrame(processFilters);
          }
        };
        requestAnimationFrame(processFilters);
      }
    }
  } catch (error) {
    console.error("Alt 拖拽复制最终处理失败:", error);
  } finally {
    canvas.renderOnAddRemove = originalRenderOnAddRemove;
    canvas.requestRenderAll();
  }
};
